// ============================================================================
// Crop Points — React Query hooks for GET / POST / DELETE crop points
// ============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../../../../services/axiosInstance';

export interface CropPoints {
  id: number;
  image_id: number;
  top_left_x: number;
  top_left_y: number;
  bottom_right_x: number;
  bottom_right_y: number;
  is_active: number;
}

export function useCropPoints(imageId: number | null) {
  return useQuery({
    queryKey: ['crop-points', imageId],
    queryFn: async () => {
      const { data } = await apiClient.get(
        `/v1/admin/content-sections/cms-images/${imageId}/crop-points`,
      );
      return (data?.payload?.results?.crop_points as CropPoints | null) ?? null;
    },
    enabled: imageId != null && imageId > 0,
    staleTime: 30_000,
  });
}

export function useSaveCropPoints() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      imageId: number;
      topLeftX: number;
      topLeftY: number;
      bottomRightX: number;
      bottomRightY: number;
    }) => {
      const { data } = await apiClient.post(
        `/v1/admin/content-sections/cms-images/${params.imageId}/crop-points`,
        {
          payload: {
            top_left_x: params.topLeftX,
            top_left_y: params.topLeftY,
            bottom_right_x: params.bottomRightX,
            bottom_right_y: params.bottomRightY,
          },
          meta: {},
        },
      );
      return (data?.payload?.results?.crop_points as CropPoints | null) ?? null;
    },
    onSuccess: (data, variables) => {
      // Use returned crop points directly — no redundant GET
      queryClient.setQueryData(['crop-points', variables.imageId], data);
      queryClient.invalidateQueries({ queryKey: ['cms-images'] });
      // Invalidate editor image resolve so cropped URLs are fetched
      queryClient.invalidateQueries({ queryKey: ['cms-image-resolve'] });
    },
  });
}

export function useDeleteCropPoints() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (imageId: number) => {
      const { data } = await apiClient.post(
        `/v1/admin/content-sections/cms-images/${imageId}/crop-points`,
        { payload: { _method: 'DELETE' }, meta: {} },
      );
      return data?.payload?.results?.success as boolean;
    },
    onSuccess: (_data, imageId) => {
      // Clear cached crop points for this image
      queryClient.setQueryData(['crop-points', imageId], null);
      queryClient.invalidateQueries({ queryKey: ['cms-images'] });
      // Invalidate editor image resolve so original URLs are restored
      queryClient.invalidateQueries({ queryKey: ['cms-image-resolve'] });
    },
  });
}
